<template>
  <b-table
      ref="table"
      v-bind="$attrs"
      v-on="$listeners">
    <template #table-busy>
      <div class="text-center my-2">
        <strong v-if="isLoading">
          <i class="fas fa-fw fa-circle-notch fa-spin"/>
        </strong>
        <div
            v-else
            class="d-flex text-danger">
          <div class="ml-auto px-2">
            <i class="fas fa-fw fa-exclamation-triangle"/>
          </div>
          <div class="mr-auto pr-2 text-left">
            <strong>{{ errorText|trans }}</strong>
          </div>
        </div>
      </div>
    </template>

    <template #head()="row">
      <div class="sort-header">
        {{ row.label }}
        <span class="sort-container">
          <i class="fas fa-fw fa-sort sort-none text-light"/>
          <i class="fas fa-fw fa-sort-up sort-asc text-primary"/>
          <i class="fas fa-fw fa-sort-down sort-desc text-primary"/>
        </span>
      </div>
    </template>

    <template
        v-for="(_, slot) of $scopedSlots"
        #[slot]="scope">
      <slot
          :name="slot"
          v-bind="scope"/>
    </template>
  </b-table>
</template>

<script lang="ts">
import {Component, Prop, Ref, Vue} from 'vue-property-decorator';
import {BTable} from 'bootstrap-vue';

@Component
export default class StoresTable extends Vue {
  /** Defines whether the table is loading */
  @Prop({default: false})
  protected isLoading!: boolean;

  @Prop({default: 'frontend.load-failed'})
  protected errorText!: string;

  @Ref()
  private readonly table!: BTable;

  /**
   * Clears the current table selection
   */
  public clearSelected(): void {
    this.table.clearSelected();
  }
}
</script>


<style scoped lang="scss">
@use 'sass:map';
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins/breakpoints";
@import "../../css/colors";

table.table {

  &.b-table-stacked {
    @each $breakpoint in map.keys($grid-breakpoints) {
      $next: breakpoint-next($breakpoint, $grid-breakpoints);
      $infix: breakpoint-infix($next, $grid-breakpoints);

      @include media-breakpoint-down($breakpoint) {
        &#{$infix} {
          ::v-deep {
            td, th {
              padding: 0.1rem;
            }

            td {
              &:before {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }

              &:not(.actions) > div {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }

              &.text-right {
                // Overwrite text right in stacked mode
                text-align: left !important;
              }
            }
          }
        }
      }
    }
  }

  ::v-deep {
    th {
      outline: none;

      &.no-interaction {
        pointer-events: none;
      }

      .sort-container {
        display: none;
        margin: 0;
        padding: 0;
      }

      &[aria-sort] {
        background: none !important;
        padding-left: 0.3rem !important;
        padding-right: 0.3rem !important;

        .sort-header {
          margin-right: 1rem;
        }

        .sort-container {
          display: inline;
          position: relative;

          i {
            display: none;
            position: absolute;
            line-height: inherit;
            top: 0;
            left: 0;

            &.sort-none {
              display: inline-block;
              color: $spanish-gray;
            }
          }
        }
      }

      &[aria-sort="ascending"] {
        .sort-container i.sort-asc {
          display: inline-block;
        }
      }

      &[aria-sort="descending"] {
        .sort-container i.sort-desc {
          display: inline-block;
        }
      }
    }
  }
}
</style>
